import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import type { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal.types'
import Sheet from 'components/utils/Sheet'
import ModalContext from 'contexts/ModalContext'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { useAppSelector } from 'hooks/reduxHooks'
import Modal from 'LuxKit/Modal/Modal'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { rem } from 'polished'
import { useCallback, useContext } from 'react'
import type { ReactNode } from 'react'
import { useIntl } from 'react-intl'
import { selectLoggedIn } from 'selectors/accountSelectors'
import styled from 'styled-components'
import useClaimFriendsAndFamilyReferral from './hooks/useClaimFriendsAndFamilyReferral'

const HowItWorksCard = styled(Sheet)`
  margin-top: ${rem(24)};
`

const OrderedList = styled.ol`
  margin: 0;
  padding-left: ${rem(24)};
`

const ListItem = styled.li`
  color: ${props => props.theme.palette.neutral.default.two};

  &:not(:last-child) {
    margin-bottom: ${rem(24)};
  }
`

interface Props {
  fnfEmailPrefix?: string
  onCancel?: () => void
  onClaim?: () => void
}

function FriendsAndFamilyClaimModal(props: Props) {
  const {
    fnfEmailPrefix,
    onCancel,
    onClaim,
  } = props
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const modalElement = useModalElementContext()
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const claimFriendsAndFamilyReferral = useClaimFriendsAndFamilyReferral({
    fnfEmailPrefix,
    onSuccess: () => {
      onClaim?.()
      modalElement.resolve()
    },
  })

  const employee = (chunks: ReactNode) => chunks
  const primaryActionText = isLoggedIn ?
      intl.formatMessage({
        id: 'x49s8lq8AjUd4wMWxdSF',
        defaultMessage: 'Claim your access',
        description: 'Primary button text for a logged-in invitee claiming friends and family access',
      }) :
      intl.formatMessage({
        id: 'Vq6vjVbmjaAiRL-W2lqF',
        defaultMessage: 'Log in to claim your access',
        description: 'Primary button text prompting the invitee to log in and claim friends and family access',
      })
  const onPrimaryActionClick = useCallback(() => {
    if (isLoggedIn) {
      claimFriendsAndFamilyReferral.mutate()
      return
    }

    showModal<AccountAccessModalResult>(<AccountAccessModal initialMode="login" />)
  }, [claimFriendsAndFamilyReferral, isLoggedIn, showModal])

  return (
    <Modal
      dismissible={false}
      size="S"
      title={intl.formatMessage({
        id: 'l1bpni9c4nB7SkjAIVU8',
        defaultMessage: 'Claim your Family & Friends access',
        description: 'Title for the modal where an invitee claims friends and family discount access',
      })}
      secondaryActionText={intl.formatMessage({
        id: '-XfjtRcieIZDz0ZllFIZ',
        defaultMessage: 'Cancel',
        description: 'Cancel button text in the friends and family claim access modal',
      })}
      primaryActionText={primaryActionText}
      isPrimaryActionDisabled={isLoggedIn && claimFriendsAndFamilyReferral.isLoading}
      onSecondaryActionClick={onCancel ? () => onCancel() : undefined}
      onPrimaryActionClick={onPrimaryActionClick}
    >
      <VerticalSpacer gap={24}>
        <BodyText variant="large" colour="neutral-two">
          {intl.formatMessage(
            {
              id: 'uDuRfqpLKvwG03Vugkiw',
              defaultMessage: 'You have been given exclusive access to Luxury Escapes’ staff-priced packages on select dates and hotels — deals that aren’t available to the public.',
              description: 'Introductory copy in the friends and family claim modal explaining who shared staff-priced access',
            },
          )}
        </BodyText>

        <HowItWorksCard background="light-background" padding={20}>
          <VerticalSpacer gap={24}>
            <Heading variant="heading4" colour="neutral-two">
              {intl.formatMessage({
                id: 'WEP4wpJjeSyAMuFIPZwY',
                defaultMessage: 'How it works',
                description: 'Section heading in the friends and family claim modal explaining access steps',
              })}
            </Heading>

            <OrderedList>
              <ListItem>
                <BodyText as="span" variant="large" colour="inherit">
                  {intl.formatMessage({
                    id: 'iN8N53E5G8OnYdJIwa22',
                    defaultMessage: 'Log in or create a Luxury Escapes account to claim your access. Make sure you use an email address you’ll book with — your Friends & Family discount will only apply to that address.',
                    description: 'First step in the friends and family claim modal explaining account and email requirements',
                  })}
                </BodyText>
              </ListItem>
              <ListItem>
                <BodyText as="span" variant="large" colour="inherit">
                  {intl.formatMessage(
                    {
                      id: 'JQ3yHGtt6lOG_y1PCagi',
                      defaultMessage: 'Your request will be sent to <employee>{employeeName}</employee> to approve. You’ll receive a confirmation email once they do.',
                      description: 'Second step in the friends and family claim modal explaining employee approval',
                    },
                    {
                      employeeName: fnfEmailPrefix,
                      employee,
                    },
                  )}
                </BodyText>
              </ListItem>
              <ListItem>
                <BodyText as="span" variant="large" colour="inherit">
                  {intl.formatMessage({
                    id: 'dfIyO3ariBTl_yWvGf4J',
                    defaultMessage: 'Once approved, you can browse and book exclusive staff-priced packages on select dates and hotels. Availability is limited.',
                    description: 'Third step in the friends and family claim modal explaining approved access duration and booking',
                  })}
                </BodyText>
              </ListItem>
            </OrderedList>

            <BodyText variant="large" colour="neutral-two">
              {intl.formatMessage({
                id: 'lbAtX2R4YnaIB0w9Zl1G',
                defaultMessage: 'Please note: no additional discounts or promo codes can be applied on top of this rate.',
                description: 'Final note in the friends and family claim modal explaining discount stacking restrictions',
              })}
            </BodyText>
          </VerticalSpacer>
        </HowItWorksCard>
      </VerticalSpacer>
    </Modal>
  )
}

export default FriendsAndFamilyClaimModal
